<template>
  <div class="py-4 space-y-6">
    <!-- Title -->
    <div class="text-center mb-6">
      <h1 class="text-xl font-bold text-foreground mb-2">
        {{ $t('report.clients_acquisition_info.title') }}
      </h1>
      <p class="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">
        {{ $t('report.clients_acquisition_info.description') }}
      </p>
    </div>

    <!-- How to read -->
    <div class="space-y-4">
      <div class="flex items-center gap-2">
        <div class="w-1 h-3.5 rounded-full" style="background: #06C8FF"></div>
        <h2 class="text-sm font-bold uppercase tracking-wide text-foreground">
          {{ $t('report.clients_acquisition_info.how_to_read') }}
        </h2>
      </div>

      <!-- Mini illustration: two lines + projection -->
      <div class="rounded-lg p-3 info-example-bg">
        <div class="relative h-24 flex items-end">
          <!-- N-1 line (grey) -->
          <svg class="absolute inset-0 w-full h-full" viewBox="0 0 200 80" preserveAspectRatio="none">
            <path d="M10,70 Q50,60 80,40 T140,15 T190,8" fill="none" stroke="rgba(100,116,139,0.35)" stroke-width="2"/>
            <!-- Current line (cyan) — lower -->
            <path d="M10,72 Q40,68 70,58 T110,45" fill="none" stroke="#06C8FF" stroke-width="2.5"/>
            <!-- Projection (cyan dotted) -->
            <path d="M110,45 Q140,35 190,25" fill="none" stroke="#06C8FF" stroke-width="2" stroke-dasharray="4 4" opacity="0.4"/>
            <!-- Today line -->
            <line x1="110" y1="5" x2="110" y2="75" stroke="rgba(100,116,139,0.2)" stroke-width="1"/>
          </svg>
        </div>
        <div class="flex items-center justify-between mt-2 px-1">
          <div class="flex items-center gap-3">
            <span class="flex items-center gap-1">
              <span class="w-2 h-2 rounded-full" style="background: #06C8FF"></span>
              <span class="text-[9px] font-semibold text-muted-foreground">{{ $t('report.clients_acquisition_info.legend_current') }}</span>
            </span>
            <span class="flex items-center gap-1">
              <span class="w-2 h-2 rounded-full" style="background: rgba(100,116,139,0.35)"></span>
              <span class="text-[9px] font-semibold text-muted-foreground">{{ $t('report.clients_acquisition_info.legend_previous') }}</span>
            </span>
          </div>
          <span class="flex items-center gap-1">
            <span class="w-3 border-t-2 border-dashed" style="border-color: #06C8FF; opacity: 0.4"></span>
            <span class="text-[9px] font-semibold text-muted-foreground">{{ $t('report.clients_acquisition_info.legend_projection') }}</span>
          </span>
        </div>
      </div>

      <!-- Reading guide items -->
      <div class="space-y-3">
        <div class="flex items-start gap-2.5">
          <span class="w-2.5 h-2.5 rounded-full shrink-0 mt-1" style="background: #06C8FF"></span>
          <div>
            <span class="text-xs font-semibold text-foreground">{{ $t('report.clients_acquisition_info.current_label') }}</span>
            <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
              {{ $t('report.clients_acquisition_info.current_desc') }}
            </p>
          </div>
        </div>

        <div class="flex items-start gap-2.5">
          <span class="w-2.5 h-2.5 rounded-full shrink-0 mt-1" style="background: rgba(100,116,139,0.5)"></span>
          <div>
            <span class="text-xs font-semibold text-foreground">{{ $t('report.clients_acquisition_info.previous_label') }}</span>
            <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
              {{ $t('report.clients_acquisition_info.previous_desc') }}
            </p>
          </div>
        </div>

        <div class="flex items-start gap-2.5">
          <span class="w-4 border-t-2 border-dashed shrink-0 mt-2" style="border-color: #06C8FF; opacity: 0.4"></span>
          <div>
            <span class="text-xs font-semibold text-foreground">{{ $t('report.clients_acquisition_info.projection_label') }}</span>
            <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
              {{ $t('report.clients_acquisition_info.projection_desc') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- How projection works -->
    <div class="space-y-3">
      <div class="flex items-center gap-2">
        <div class="w-1 h-3.5 rounded-full" style="background: #06C8FF"></div>
        <h2 class="text-sm font-bold uppercase tracking-wide text-foreground">
          {{ $t('report.clients_acquisition_info.projection_section') }}
        </h2>
      </div>

      <p class="text-xs text-muted-foreground leading-relaxed ml-3">
        {{ $t('report.clients_acquisition_info.projection_intro') }}
      </p>

      <div class="space-y-2.5">
        <div class="info-metric-item">
          <span class="info-metric-label">{{ $t('report.clients_acquisition_info.method_trend_label') }}</span>
          <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
            {{ $t('report.clients_acquisition_info.method_trend_desc') }}
          </p>
        </div>

        <div class="info-metric-item">
          <span class="info-metric-label">{{ $t('report.clients_acquisition_info.method_seasonal_label') }}</span>
          <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
            {{ $t('report.clients_acquisition_info.method_seasonal_desc') }}
          </p>
        </div>

        <div class="info-metric-item">
          <span class="info-metric-label">{{ $t('report.clients_acquisition_info.method_blend_label') }}</span>
          <p class="text-xs text-muted-foreground leading-relaxed mt-0.5">
            {{ $t('report.clients_acquisition_info.method_blend_desc') }}
          </p>
        </div>
      </div>

      <!-- Caution note -->
      <div class="info-note rounded-lg px-3 py-2.5 mt-3">
        <p class="text-xs text-muted-foreground leading-relaxed">
          <span class="font-semibold text-foreground">{{ $t('report.clients_acquisition_info.caution_label') }}</span>
          {{ $t('report.clients_acquisition_info.caution_desc') }}
        </p>
      </div>
    </div>

  </div>
</template>

<script setup lang="ts">
// Content-only component — no props needed.
// Injected into a BottomSheet from CenterClientsReport.
</script>

<style scoped>
.info-note {
  background: rgba(245, 158, 11, 0.06);
  border: 1px solid rgba(245, 158, 11, 0.15);
}
:is(.dark *).info-note {
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.12);
}
.info-example-bg {
  background: rgba(107, 114, 128, 0.06);
  border: 1px solid rgba(107, 114, 128, 0.1);
}
:is(.dark *).info-example-bg {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.info-metric-item {
  padding-left: 0.75rem;
  border-left: 2px solid rgba(107, 114, 128, 0.15);
}
:is(.dark *).info-metric-item {
  border-left-color: rgba(255, 255, 255, 0.1);
}
.info-metric-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--foreground);
}
</style>
